/** System status → Graphics (0.92.0): the PRISM Graphics editor's address (from the configuration) and the default hold of a new graphic. */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '../../../api/client';
import { queryKeys } from '../../../api/queryKeys';
import { toast } from '../../../components/Toasts';

interface GraphicsSettingsDTO { defaultHoldSeconds: number; editorOrigin: string | null }

export function GraphicsSettings() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['system-graphics-settings'], queryFn: () => api<GraphicsSettingsDTO>('/api/v1/system/graphics-settings') });
  const [hold, setHold] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const d = q.data;
  const value = hold ?? (d ? String(d.defaultHoldSeconds) : '');
  async function save() {
    setBusy(true);
    try {
      await api('/api/v1/system/graphics-settings', { method: 'PUT', json: { defaultHoldSeconds: Number(value) } });
      await qc.invalidateQueries({ queryKey: ['system-graphics-settings'] });
      await qc.invalidateQueries({ queryKey: queryKeys.graphicsSettings() });
      setHold(null);
      toast('Saved');
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  }
  if (!d) return <div className="skeleton" style={{ height: 160 }} />;
  return (
    <section className="panel-card" style={{ maxWidth: 760 }} data-testid="graphics-settings">
      <p className="section-label">PRISM Graphics</p>
      <div className="field"><label>Editor address</label>
        <span className="mono readonly-value" data-testid="graphics-editor-origin">{d.editorOrigin ?? 'Not configured'}</span>
        <span className="help">Set in the configuration (PRISM_GRAPHICS_EDITOR_ORIGIN) for the API and the web tier. Without it, the Graphics area lists graphics but cannot make or edit them.</span>
      </div>
      <div className="field"><label htmlFor="gfx-hold">Default hold of a new graphic</label>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input id="gfx-hold" className="input" type="number" min={0.5} max={600} step={0.5} style={{ width: 100 }} value={value} onChange={(e) => setHold(e.target.value)} /> seconds
        </div>
        <span className="help">How long a graphic made in PRISM Graphics stays on screen between its in and out animations when it is placed on a timeline (4 seconds unless changed here). PRISM Graphics reads it too.</span>
      </div>
      <div><button className="btn primary" disabled={busy || hold === null || !(Number(value) >= 0.5 && Number(value) <= 600)} onClick={() => void save()} data-testid="graphics-settings-save">Save</button></div>
    </section>
  );
}
